import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";

type TooltipArrowDirection = "up" | "right" | "down" | "left";
type TooltipTextAlign = "left" | "center" | "right";

interface TooltipProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "content"
> {
  text?: string;
  content?: React.ReactNode;
  iconName?: string;
  arrow?: TooltipArrowDirection;
  textAlign?: TooltipTextAlign;
  backgroundColor?: string;
  textColor?: string;
  minWidth?: number;
  maxWidth?: number;
  arrowOffsetX?: number;
  arrowOffsetY?: number;
}

interface TooltipHoverProps extends TooltipProps {
  children: React.ReactNode;
  offset?: number;
  fullWidth?: boolean;
  anchorRef?: React.RefObject<HTMLElement>;
}

const TOOLTIP_PADDING_VERTICAL = 16;
const TOOLTIP_PADDING_HORIZONTAL = 12;
const TOOLTIP_BORDER_RADIUS = 16;
const ARROW_SIZE = 18;
const DEFAULT_OFFSET = 12;

const getTooltipContainerProps = (
  props: TooltipProps,
): React.HTMLAttributes<HTMLDivElement> => {
  const {
    text: _text,
    content: _content,
    iconName: _iconName,
    arrow: _arrow,
    textAlign: _textAlign,
    backgroundColor: _backgroundColor,
    textColor: _textColor,
    minWidth: _minWidth,
    maxWidth: _maxWidth,
    arrowOffsetX: _arrowOffsetX,
    arrowOffsetY: _arrowOffsetY,
    ...rest
  } = props;

  return rest;
};

const getTooltipPropsFromHover = (props: TooltipHoverProps): TooltipProps => {
  const {
    children: _children,
    offset: _offset,
    fullWidth: _fullWidth,
    anchorRef: _anchorRef,
    ...rest
  } = props;
  return rest;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

// Used as the initial fallback width before the bubble is first measured
const SAFE_BUBBLE_WIDTH = 253;

let tooltipIdCounter = 0;

interface ArrowOffset {
  x: number;
  y: number;
}

interface BubbleDimensions {
  width: number;
  height: number;
}

const getArrowOffset = (
  rect: DOMRect,
  placement: TooltipArrowDirection,
  bubbleDimensions: BubbleDimensions,
): ArrowOffset => {
  if (placement === "up" || placement === "down") {
    const halfW = bubbleDimensions.width / 2;
    const centerX = rect.left + rect.width / 2;
    const clampedX = clamp(centerX, halfW, window.innerWidth - halfW);
    const rawOffsetX = centerX - clampedX;
    const maxOffsetX = Math.max(0, halfW - ARROW_SIZE);
    return { x: clamp(rawOffsetX, -maxOffsetX, maxOffsetX), y: 0 };
  }
  const halfH = bubbleDimensions.height / 2;
  const centerY = rect.top + rect.height / 2;
  const clampedY = clamp(centerY, halfH, window.innerHeight - halfH);
  const rawOffsetY = centerY - clampedY;
  const maxOffsetY = Math.max(0, halfH - ARROW_SIZE);
  return { x: 0, y: clamp(rawOffsetY, -maxOffsetY, maxOffsetY) };
};

const getPortalStyle = (
  rect: DOMRect,
  placement: TooltipArrowDirection,
  offset: number,
  bubbleDimensions: BubbleDimensions,
): React.CSSProperties => {
  const halfW = bubbleDimensions.width / 2;
  const halfH = bubbleDimensions.height / 2;
  switch (placement) {
    case "down":
      return {
        bottom: window.innerHeight - rect.top + offset,
        left: clamp(
          rect.left + rect.width / 2,
          halfW,
          window.innerWidth - halfW,
        ),
      };
    case "up":
      return {
        top: clamp(rect.bottom + offset, 0, window.innerHeight),
        left: clamp(
          rect.left + rect.width / 2,
          halfW,
          window.innerWidth - halfW,
        ),
      };
    case "left":
      return {
        left: clamp(
          rect.right + offset,
          0,
          window.innerWidth - bubbleDimensions.width,
        ),
        top: clamp(
          rect.top + rect.height / 2,
          halfH,
          window.innerHeight - halfH,
        ),
      };
    case "right":
      return {
        right: clamp(
          window.innerWidth - rect.left + offset,
          bubbleDimensions.width,
          window.innerWidth,
        ),
        top: clamp(
          rect.top + rect.height / 2,
          halfH,
          window.innerHeight - halfH,
        ),
      };
  }
};

/**
 * Tooltip bubble that renders optional icon, text, and an arrow.
 */
export const Tooltip = (props: TooltipProps): React.ReactElement => {
  const {
    text,
    content,
    iconName,
    arrow: arrowProp,
    textAlign: textAlignProp,
    backgroundColor: backgroundColorProp,
    textColor: textColorProp,
    minWidth: minWidthProp,
    maxWidth: maxWidthProp,
    arrowOffsetX,
    arrowOffsetY,
  } = props;
  const hasIcon = Boolean(iconName);
  const resolvedContent = content ?? text ?? "";
  const arrow = arrowProp ?? "down";
  const textAlign = textAlignProp ?? (hasIcon ? "left" : "center");
  const backgroundColor = backgroundColorProp ?? ColorV2.Blue6;
  const textColor = textColorProp ?? ColorV2.Blue3;
  const minWidth = minWidthProp ?? 140;
  const maxWidth = maxWidthProp ?? 220;
  const offsetX = arrowOffsetX ?? 0;
  const offsetY = arrowOffsetY ?? 0;
  const containerProps = getTooltipContainerProps(props);

  return (
    <Container
      $hasIcon={hasIcon}
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      {...containerProps}
    >
      {iconName && (
        <IconWrapper>
          <CustomIcon name={iconName} size="24px" color={ColorV2.Blue3} />
        </IconWrapper>
      )}
      <Content $textAlign={textAlign} $minWidth={minWidth} $maxWidth={maxWidth}>
        {typeof resolvedContent === "string" ? (
          <Text>{resolvedContent}</Text>
        ) : (
          resolvedContent
        )}
      </Content>
      <Arrow
        $direction={arrow}
        $backgroundColor={backgroundColor}
        $offsetX={offsetX}
        $offsetY={offsetY}
      />
    </Container>
  );
};

/**
 * Wraps a trigger and shows the tooltip bubble on hover via a fixed-position portal,
 * so it is never clipped by overflow-hidden/scroll ancestors.
 */
export const TooltipHover = (props: TooltipHoverProps): React.ReactElement => {
  const {
    children,
    arrow: arrowProp,
    offset: offsetProp,
    backgroundColor,
    textColor,
    fullWidth,
    anchorRef,
  } = props;
  const arrow = arrowProp ?? "down";
  const offset = offsetProp ?? DEFAULT_OFFSET;
  const tooltipProps = getTooltipPropsFromHover(props);

  const [tooltipId] = useState(() => `tooltip-${++tooltipIdCounter}`);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [isPressedOpen, setIsPressedOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [bubbleDimensions, setBubbleDimensions] = useState<BubbleDimensions>({
    width: SAFE_BUBBLE_WIDTH,
    height: 0,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);

  const isOpen = isHoverOpen || isPressedOpen;
  const arrowOffset = rect
    ? getArrowOffset(rect, arrow, bubbleDimensions)
    : null;

  const updateRect = useCallback(() => {
    const element = anchorRef?.current ?? triggerRef.current;
    if (element) {
      setRect(element.getBoundingClientRect());
    }
  }, [anchorRef]);

  // Measure the rendered bubble dimensions before first paint
  useLayoutEffect(() => {
    if (!rect || !bubbleRef.current) return;
    const bubbleRect = bubbleRef.current.getBoundingClientRect();
    setBubbleDimensions({ width: bubbleRect.width, height: bubbleRect.height });
    // bubbleDimensions intentionally omitted from deps — adding it would loop
  }, [rect]);

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [isOpen, updateRect]);

  useEffect(() => {
    if (!isPressedOpen) return;
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        setIsPressedOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setIsPressedOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isPressedOpen]);

  const clonedChildren =
    React.Children.count(children) === 1 && React.isValidElement(children)
      ? React.cloneElement(children as React.ReactElement, {
          "aria-describedby": tooltipId,
        })
      : children;

  return (
    <HoverWrap
      ref={wrapperRef}
      onMouseEnter={() => {
        updateRect();
        setIsHoverOpen(true);
      }}
      onMouseLeave={() => {
        setIsHoverOpen(false);
        setIsPressedOpen(false);
      }}
      onFocus={() => {
        updateRect();
        setIsHoverOpen(true);
      }}
      onBlur={() => {
        setIsHoverOpen(false);
        setIsPressedOpen(false);
      }}
      $fullWidth={Boolean(fullWidth)}
    >
      <Trigger
        ref={triggerRef}
        onClick={() => {
          updateRect();
          setIsPressedOpen((prev) => !prev);
          setIsHoverOpen(true);
        }}
        $fullWidth={Boolean(fullWidth)}
      >
        {clonedChildren}
      </Trigger>
      {createPortal(
        <PortalBubble
          ref={bubbleRef}
          id={tooltipId}
          role="tooltip"
          $placement={arrow}
          $isOpen={isOpen && rect !== null}
          style={
            rect
              ? getPortalStyle(rect, arrow, offset, bubbleDimensions)
              : undefined
          }
        >
          <Tooltip
            {...tooltipProps}
            arrow={arrow}
            backgroundColor={backgroundColor}
            textColor={textColor}
            arrowOffsetX={arrowOffset?.x ?? 0}
            arrowOffsetY={arrowOffset?.y ?? 0}
          />
        </PortalBubble>,
        document.body,
      )}
    </HoverWrap>
  );
};

const Container = styled.div<{
  $hasIcon: boolean;
  $backgroundColor: string;
  $textColor: string;
}>`
  ${TypographyV2.s1}
  position: relative;
  width: fit-content;
  padding: ${TOOLTIP_PADDING_VERTICAL}px ${TOOLTIP_PADDING_HORIZONTAL}px;
  border-radius: ${TOOLTIP_BORDER_RADIUS}px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const Content = styled.div<{
  $textAlign: TooltipTextAlign;
  $minWidth: number;
  $maxWidth: number;
}>`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 auto;
  min-width: ${(props) => props.$minWidth}px;
  max-width: ${(props) => props.$maxWidth}px;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  text-align: ${(props) => props.$textAlign};
  align-items: ${(props) => {
    if (props.$textAlign === "right") return "flex-end";
    if (props.$textAlign === "center") return "center";
    return "flex-start";
  }};
`;

const Text = styled.span`
  ${TypographyV2.s1}
  color: inherit;
  white-space: pre-line;
`;

const Arrow = styled.span<{
  $direction: TooltipArrowDirection;
  $backgroundColor: string;
  $offsetX: number;
  $offsetY: number;
}>`
  position: absolute;
  width: ${ARROW_SIZE}px;
  height: ${ARROW_SIZE}px;
  background-color: ${(props) => props.$backgroundColor};
  transform: rotate(45deg);

  ${(props) =>
    props.$direction === "up" &&
    css`
      top: -${ARROW_SIZE / 2}px;
      left: calc(50% + ${props.$offsetX}px);
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "down" &&
    css`
      bottom: -${ARROW_SIZE / 2}px;
      left: calc(50% + ${props.$offsetX}px);
      transform: translateX(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "left" &&
    css`
      left: -${ARROW_SIZE / 2}px;
      top: calc(50% + ${props.$offsetY}px);
      transform: translateY(-50%) rotate(45deg);
    `}

  ${(props) =>
    props.$direction === "right" &&
    css`
      right: -${ARROW_SIZE / 2}px;
      top: calc(50% + ${props.$offsetY}px);
      transform: translateY(-50%) rotate(45deg);
    `}
`;

const PortalBubble = styled.div<{
  $placement: TooltipArrowDirection;
  $isOpen: boolean;
}>`
  position: fixed;
  z-index: 9999;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  pointer-events: ${(props) => (props.$isOpen ? "auto" : "none")};
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;

  ${(props) =>
    props.$placement === "down" &&
    css`
      transform: translateX(-50%) translateY(${props.$isOpen ? 0 : 4}px);
    `}
  ${(props) =>
    props.$placement === "up" &&
    css`
      transform: translateX(-50%) translateY(${props.$isOpen ? 0 : -4}px);
    `}
  ${(props) =>
    props.$placement === "left" &&
    css`
      transform: translateY(-50%) translateX(${props.$isOpen ? 0 : 4}px);
    `}
  ${(props) =>
    props.$placement === "right" &&
    css`
      transform: translateY(-50%) translateX(${props.$isOpen ? 0 : -4}px);
    `}
`;

const HoverWrap = styled.div<{ $fullWidth: boolean }>`
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  align-self: flex-start;
  width: ${(props) => (props.$fullWidth ? "100%" : "fit-content")};
  max-width: 100%;
`;

const Trigger = styled.span<{ $fullWidth: boolean }>`
  position: relative;
  display: ${(props) => (props.$fullWidth ? "flex" : "inline-flex")};
  align-items: center;
  width: ${(props) => (props.$fullWidth ? "100%" : "auto")};
`;
